// Small timing helpers with no DOM dependencies (unit-tested in test/timing.test.ts).

export const BACKOFF_BASE_MS = 1000;
export const BACKOFF_MAX_MS = 15_000;

/** Exponential backoff: 1s, 2s, 4s, 8s, then capped at 15s. `attempt` starts at 0. */
export function backoffDelay(attempt: number): number {
  return Math.min(BACKOFF_BASE_MS * 2 ** attempt, BACKOFF_MAX_MS);
}

/** Milliseconds as m:ss, rounding up so the clock only shows 0:00 when time is really up. */
export function formatClock(ms: number): string {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export interface Throttled<A extends unknown[]> {
  (...args: A): void;
  /** Drops any pending trailing call. */
  cancel(): void;
}

/**
 * Calls `fn` at most once per `intervalMs`. The first call runs immediately; calls
 * inside the interval collapse into one trailing call with the latest arguments.
 */
export function throttle<A extends unknown[]>(fn: (...args: A) => void, intervalMs: number): Throttled<A> {
  let last = -Infinity;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pending: A | null = null;

  const throttled = ((...args: A) => {
    const wait = last + intervalMs - Date.now();
    if (wait <= 0 && !timer) {
      last = Date.now();
      fn(...args);
      return;
    }
    pending = args;
    timer ??= setTimeout(() => {
      timer = undefined;
      if (!pending) return;
      const latest = pending;
      pending = null;
      last = Date.now();
      fn(...latest);
    }, Math.max(wait, 0));
  }) as Throttled<A>;

  throttled.cancel = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    pending = null;
  };
  return throttled;
}
